import * as RadixPopover from '@radix-ui/react-popover';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  memo,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { WorkspaceAgentGuidedVariantSizeCombobox } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeCombobox';
import { WorkspaceAgentGuidedVariantSmartResizeMenu } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSmartResizeMenu';
import {
  expandedWorkspaceAgentSmartResizeGroups,
  searchWorkspaceAgentSmartResize,
  toggleWorkspaceAgentSmartResizeSet,
  toggleWorkspaceAgentSmartResizeSize,
  WORKSPACE_AGENT_SMART_RESIZE_CHANNELS,
  WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES,
  WORKSPACE_AGENT_SMART_RESIZE_SHAPES,
  type WorkspaceAgentSmartResizeSavedSize,
  type WorkspaceAgentSmartResizeSize,
  type WorkspaceAgentSmartResizeSizeSet,
  workspaceAgentSmartResizeChannelToSize,
  workspaceAgentSmartResizeSizeKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

type PopoverDismissEvent = {
  detail: { originalEvent: Event };
  preventDefault: () => void;
};

export type WorkspaceAgentGuidedVariantSmartResizeFieldProps = {
  isSavedCatalogLoading: boolean;
  onSizesChange: (sizes: WorkspaceAgentSmartResizeSize[]) => void;
  savedSets: readonly WorkspaceAgentSmartResizeSizeSet[];
  savedSizes: readonly WorkspaceAgentSmartResizeSavedSize[];
  sizes: readonly WorkspaceAgentSmartResizeSize[];
};

export const WorkspaceAgentGuidedVariantSmartResizeField = memo(
  ({
    isSavedCatalogLoading,
    onSizesChange,
    savedSets,
    savedSizes,
    sizes,
  }: WorkspaceAgentGuidedVariantSmartResizeFieldProps) => {
    const listId = useId();
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [isExactMode, setIsExactMode] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState(() => expandedWorkspaceAgentSmartResizeGroups(sizes));
    const isAtCap = sizes.length >= WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES;
    const normalizedQuery = query.trim();
    const search = searchWorkspaceAgentSmartResize({ query: normalizedQuery, savedSets, savedSizes });
    const hasSearch = normalizedQuery.length > 0;
    const hasSearchResults =
      search.typed !== null ||
      search.shapes.length > 0 ||
      search.sets.length > 0 ||
      search.savedSizes.length > 0 ||
      search.channels.length > 0;

    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const openMenu = useCallback(() => {
      if (isOpenRef.current) {
        return;
      }

      setExpandedGroups(expandedWorkspaceAgentSmartResizeGroups(sizes));
      setIsOpen(true);
    }, [sizes]);

    const handleOpenChange = useCallback((open: boolean) => {
      if (!open) {
        setIsOpen(false);
      }
    }, []);

    const handleOpenAutoFocus = useCallback((event: Event) => {
      event.preventDefault();
    }, []);

    const handleCloseAutoFocus = useCallback((event: Event) => {
      event.preventDefault();
    }, []);

    const dismissSearchOrMenu = useCallback(() => {
      if (query.trim()) {
        setQuery('');
        setIsExactMode(false);
        return;
      }

      setIsExactMode(false);
      setIsOpen(false);
    }, [query]);

    // The combobox lives outside the portaled content, so Escape has to be handled there and here.
    // A query clears first; the next Escape closes.
    const handleEscapeKeyDown = useCallback(
      (event: Parameters<NonNullable<RadixPopover.PopoverContentProps['onEscapeKeyDown']>>[0]) => {
        event.preventDefault();
        dismissSearchOrMenu();
      },
      [dismissSearchOrMenu],
    );

    const dismissWhenInsideField = useCallback((event: PopoverDismissEvent) => {
      const target = event.detail.originalEvent.target;

      if (target instanceof Node && rootRef.current?.contains(target)) {
        event.preventDefault();
      }
    }, []);

    // Focusing the field opens the menu. That focus is outside the portaled content, so without this the
    // opening click closes the menu and the click opens it again.
    const handleFocusOutside = useCallback(
      (event: Parameters<NonNullable<RadixPopover.PopoverContentProps['onFocusOutside']>>[0]) => {
        dismissWhenInsideField(event);
      },
      [dismissWhenInsideField],
    );

    // A press on the field stays in the combobox. A press anywhere else only closes the menu.
    const handlePointerDownOutside = useCallback(
      (event: Parameters<NonNullable<RadixPopover.PopoverContentProps['onPointerDownOutside']>>[0]) => {
        dismissWhenInsideField(event);
      },
      [dismissWhenInsideField],
    );

    const handleToggleSize = useCallback(
      (size: WorkspaceAgentSmartResizeSize) => {
        onSizesChange(toggleWorkspaceAgentSmartResizeSize(sizes, size).sizes);
        setQuery('');
        setIsExactMode(false);
      },
      [onSizesChange, sizes],
    );

    const handleToggleSet = useCallback(
      (sizeSet: WorkspaceAgentSmartResizeSizeSet) => {
        onSizesChange(toggleWorkspaceAgentSmartResizeSet(sizes, sizeSet));
        setQuery('');
      },
      [onSizesChange, sizes],
    );

    const handleToggleGroup = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      const groupId = event.currentTarget.dataset.groupId;

      if (!groupId) {
        return;
      }

      setExpandedGroups((current) => {
        const next = new Set(current);

        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }

        return next;
      });
    }, []);

    const handleExactSize = useCallback(() => {
      setIsExactMode(true);
      setQuery('');
      inputRef.current?.focus();
    }, []);

    const handleSizeClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const sizeId = event.currentTarget.dataset.sizeId;

        if (!sizeId) {
          return;
        }

        const channel = WORKSPACE_AGENT_SMART_RESIZE_CHANNELS.find((item) => item.id === sizeId);

        if (channel) {
          handleToggleSize(workspaceAgentSmartResizeChannelToSize(channel));
          return;
        }

        const shape = WORKSPACE_AGENT_SMART_RESIZE_SHAPES.find((item) => item.id === sizeId);

        if (shape) {
          handleToggleSize(shape.size);
          return;
        }

        const saved = savedSizes.find((item) => item.id === sizeId);

        if (saved) {
          handleToggleSize(saved.size);
        }
      },
      [handleToggleSize, savedSizes],
    );

    const handleSetClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const setId = event.currentTarget.dataset.setId;
        const sizeSet = savedSets.find((item) => item.id === setId);

        if (sizeSet) {
          handleToggleSet(sizeSet);
        }
      },
      [handleToggleSet, savedSets],
    );

    const handleRemovePill = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const sizeKey = event.currentTarget.dataset.sizeKey;
        const size = sizes.find((item) => workspaceAgentSmartResizeSizeKey(item) === sizeKey);

        if (size) {
          handleToggleSize(size);
        }
      },
      [handleToggleSize, sizes],
    );

    const handleQueryChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setQuery(event.currentTarget.value);
        openMenu();
      },
      [openMenu],
    );

    const handleMenuToggle = useCallback(() => {
      if (isOpen) {
        setIsOpen(false);
        return;
      }

      openMenu();
    }, [isOpen, openMenu]);

    const handleAddTyped = useCallback(() => {
      if (search.typed) {
        handleToggleSize(search.typed);
      }
    }, [handleToggleSize, search.typed]);

    const handleInputKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          dismissSearchOrMenu();
          return;
        }

        if (event.key !== 'Enter' || event.nativeEvent.isComposing) {
          return;
        }

        event.preventDefault();

        if (search.typed) {
          handleToggleSize(search.typed);
          return;
        }

        const shape = search.shapes[0];

        if (shape) {
          handleToggleSize(shape.size);
          return;
        }

        const sizeSet = search.sets[0];

        if (sizeSet) {
          handleToggleSet(sizeSet);
          return;
        }

        const saved = search.savedSizes[0];

        if (saved) {
          handleToggleSize(saved.size);
          return;
        }

        const channel = search.channels[0];

        if (channel) {
          handleToggleSize(workspaceAgentSmartResizeChannelToSize(channel));
        }
      },
      [dismissSearchOrMenu, handleToggleSet, handleToggleSize, search],
    );

    const placeholder = useMemo(() => {
      if (isExactMode) {
        return 'Type W × H, e.g. 1080 × 1350';
      }

      if (sizes.length > 0) {
        return isAtCap ? '' : 'Add a size';
      }

      return 'Pick a channel or shape, or write W × H';
    }, [isAtCap, isExactMode, sizes.length]);

    return (
      <RadixPopover.Root onOpenChange={handleOpenChange} open={isOpen}>
        <RadixPopover.Anchor asChild>
          <div className="relative" ref={rootRef}>
            <WorkspaceAgentGuidedVariantSizeCombobox
              inputRef={inputRef}
              isOpen={isOpen}
              listId={listId}
              onInputKeyDown={handleInputKeyDown}
              onMenuToggle={handleMenuToggle}
              onOpen={openMenu}
              onQueryChange={handleQueryChange}
              onRemovePill={handleRemovePill}
              placeholder={placeholder}
              query={query}
              sizes={sizes}
            />
          </div>
        </RadixPopover.Anchor>
        {isOpen ? (
          <RadixPopover.Portal>
            <RadixPopover.Content
              align="start"
              className="z-portal flex max-h-[min(24rem,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-8 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 shadow-popover outline-none"
              collisionPadding={8}
              data-preserves-selection
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_MENU"
              onCloseAutoFocus={handleCloseAutoFocus}
              onEscapeKeyDown={handleEscapeKeyDown}
              onFocusOutside={handleFocusOutside}
              onOpenAutoFocus={handleOpenAutoFocus}
              onPointerDownOutside={handlePointerDownOutside}
              side="bottom"
              sideOffset={4}
            >
              <WorkspaceAgentGuidedVariantSmartResizeMenu
                expandedGroups={expandedGroups}
                hasSearch={hasSearch}
                hasSearchResults={hasSearchResults}
                isAtCap={isAtCap}
                isExactMode={isExactMode}
                isSavedCatalogLoading={isSavedCatalogLoading}
                listId={listId}
                onAddTyped={handleAddTyped}
                onExactSize={handleExactSize}
                onSelectSet={handleSetClick}
                onSelectSize={handleSizeClick}
                onToggleGroup={handleToggleGroup}
                savedSets={savedSets}
                savedSizes={savedSizes}
                search={search}
                sizes={sizes}
              />
            </RadixPopover.Content>
          </RadixPopover.Portal>
        ) : null}
      </RadixPopover.Root>
    );
  },
);

WorkspaceAgentGuidedVariantSmartResizeField.displayName = 'WorkspaceAgentGuidedVariantSmartResizeField';
